<template>
  <CustomerLayout>
    <div class="space-y-6">
      <div class="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-6">
        <div>
          <h2 class="text-lg font-bold text-slate-800">Pengaturan Akun & Metode Login</h2>
          <p class="text-xs text-slate-500 mt-0.5">Kelola identitas Google dan WhatsApp yang terhubung dengan akun Manza PPOB Anda.</p>
        </div>

        <!-- Multi-Provider Summary Badge -->
        <div v-if="bothLinked" class="p-3 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-semibold rounded-xl flex items-center gap-2">
          <svg class="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Kamu dapat masuk menggunakan Google atau WhatsApp.</span>
        </div>

        <!-- Flash Message -->
        <div v-if="$page.props.flash.status" class="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-xl">
          {{ $page.props.flash.status }}
        </div>

        <!-- Linked Identities List -->
        <div class="space-y-3">
          <h3 class="text-xs font-bold text-slate-700 uppercase tracking-wider">Metode Login</h3>

          <!-- Google Identity Card -->
          <div class="p-4 rounded-xl border border-slate-200 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">G</div>
              <div>
                <p class="text-sm font-semibold text-slate-800">Google</p>
                <p class="text-xs text-slate-500">{{ googleIdentity ? googleIdentity.verified_email : 'Belum terhubung' }}</p>
              </div>
            </div>
            <span v-if="googleIdentity" class="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">Terverifikasi</span>
            <a v-else href="/auth/google" class="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition">
              Hubungkan Google
            </a>
          </div>

          <!-- WhatsApp Identity Card -->
          <div class="p-4 rounded-xl border border-slate-200 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">WA</div>
              <div>
                <p class="text-sm font-semibold text-slate-800">WhatsApp</p>
                <p class="text-xs text-slate-500">{{ whatsappIdentity ? whatsappIdentity.verified_phone : 'Belum terhubung' }}</p>
              </div>
            </div>
            <span v-if="whatsappIdentity" class="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">Terverifikasi</span>
            <button v-else @click="showLinkWaModal = true" class="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition">
              Tautkan WhatsApp
            </button>
          </div>
        </div>
      </div>

      <!-- Pending Account Merge Preview Modal -->
      <div v-if="pending_merge" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl border border-slate-100">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-lg">!</div>
            <div>
              <h3 class="text-base font-bold text-slate-800">Penggabungan Akun Manza PPOB</h3>
              <p class="text-xs text-slate-500">Konfirmasi penggabungan dua akun yang terverifikasi milik Anda.</p>
            </div>
          </div>

          <div class="p-3.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl space-y-1">
            <p class="font-bold">Konflik Identitas Terdeteksi</p>
            <p>Metode login ini sudah terhubung ke akun Manza PPOB lain. Verifikasi kedua akun untuk melanjutkan penggabungan.</p>
          </div>

          <!-- Merge Details Table -->
          <div class="border border-slate-200 rounded-xl overflow-hidden text-xs divide-y divide-slate-100">
            <div class="p-3 bg-slate-50 font-semibold text-slate-700 flex justify-between">
              <span>Akun Utama (Surviving Account)</span>
              <span class="text-emerald-600 font-bold">Tetap Aktif</span>
            </div>
            <div class="p-3 flex justify-between">
              <span class="text-slate-500">Identitas Sumber (Source)</span>
              <span class="font-mono text-slate-800 font-semibold">{{ pending_merge.source_masked_identifier }}</span>
            </div>
            <div class="p-3 flex justify-between">
              <span class="text-slate-500">Saldo Dompet Utama</span>
              <span class="font-semibold text-slate-800">Rp {{ pending_merge.survivor_balance_minor.toLocaleString('id-ID') }}</span>
            </div>
            <div class="p-3 flex justify-between">
              <span class="text-slate-500">Saldo Dompet Sumber</span>
              <span class="font-semibold text-slate-800">Rp {{ pending_merge.source_balance_minor.toLocaleString('id-ID') }}</span>
            </div>
          </div>

          <div class="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl">
            <strong>Penting:</strong> Setelah penggabungan selesai, semua sesi login akan dikeluarkan dan Anda harus masuk kembali.
          </div>

          <div class="flex gap-2 justify-end pt-2">
            <button @click="confirmMergeForm.post('/settings/merge/confirm')" :disabled="confirmMergeForm.processing" class="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition shadow-sm">
              Konfirmasi & Gabungkan Akun
            </button>
          </div>
        </div>
      </div>

      <!-- Modal Link WhatsApp -->
      <div v-if="showLinkWaModal" class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4 shadow-xl">
          <h3 class="text-base font-bold text-slate-800">Tautkan Nomor WhatsApp</h3>

          <form v-if="!otpSent" @submit.prevent="sendLinkOtp" class="space-y-4">
            <div>
              <label class="block text-xs font-semibold text-slate-700 mb-1">Nomor WhatsApp</label>
              <input v-model="linkForm.phone_number" type="tel" placeholder="081234567890" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div class="flex gap-2 justify-end">
              <button type="button" @click="showLinkWaModal = false" class="px-4 py-2 text-xs font-medium text-slate-600">Batal</button>
              <button type="submit" :disabled="linkForm.processing" class="px-4 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Kirim OTP</button>
            </div>
          </form>

          <form v-else @submit.prevent="verifyLinkOtp" class="space-y-4">
            <div>
              <label class="block text-xs font-semibold text-slate-700 mb-1">Masukan Kode OTP</label>
              <input v-model="verifyForm.otp" type="text" maxlength="6" placeholder="123456" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-center font-mono font-bold text-base focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div class="flex gap-2 justify-end">
              <button type="button" @click="showLinkWaModal = false" class="px-4 py-2 text-xs font-medium text-slate-600">Batal</button>
              <button type="submit" :disabled="verifyForm.processing" class="px-4 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Verifikasi & Tautkan</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </CustomerLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useForm } from '@inertiajs/vue3';
import CustomerLayout from '@/Layouts/CustomerLayout.vue';

const props = defineProps({
  user: Object,
  pending_merge: Object,
});

const showLinkWaModal = ref(false);
const otpSent = ref(false);

const googleIdentity = computed(() => props.user.identities.find(i => i.provider === 'google'));
const whatsappIdentity = computed(() => props.user.identities.find(i => i.provider === 'whatsapp'));
const bothLinked = computed(() => !!googleIdentity.value && !!whatsappIdentity.value);

const linkForm = useForm({ phone_number: '' });
const verifyForm = useForm({ phone_number: '', otp: '' });
const confirmMergeForm = useForm({ reference: props.pending_merge?.reference ?? '' });

const sendLinkOtp = () => {
  linkForm.post('/settings/whatsapp/link', {
    onSuccess: () => {
      otpSent.value = true;
      verifyForm.phone_number = linkForm.phone_number;
    },
  });
};

const verifyLinkOtp = () => {
  verifyForm.post('/settings/whatsapp/verify', {
    onSuccess: () => {
      showLinkWaModal.value = false;
      otpSent.value = false;
    },
  });
};
</script>
